/**
 * 券规则文案解析（弹窗分段展示）
 */

import { parseUserCouponApplicableScenes } from "./normalizeUserCoupon";

function parseJsonObject(str) {
  if (str == null || str === "") return null;
  if (typeof str === "object") return str;
  try {
    const parsed = JSON.parse(String(str));
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/** 从 descriptionI18n JSON 中取当前语言文案 */
export function pickCouponI18nText(i18nRaw, locale) {
  const map = parseJsonObject(i18nRaw);
  if (!map) return "";
  const loc = String(locale || "").toLowerCase();
  const candidates = [
    loc,
    loc.replace("_", "-"),
    loc.split("-")[0],
    "en",
    "en-us",
    "zh-cn",
    "cn",
  ].filter(Boolean);
  for (const key of candidates) {
    const hit = Object.entries(map).find(([k]) => String(k).toLowerCase() === key);
    if (hit && hit[1] != null && String(hit[1]).trim()) return String(hit[1]).trim();
  }
  const first = Object.values(map).find((v) => v != null && String(v).trim());
  return first != null ? String(first).trim() : "";
}

function normalizeSceneKey(sceneCode) {
  return String(sceneCode || "")
    .replace(/[-_\s]/g, "")
    .toUpperCase();
}

/** 按场景码在 sceneRuntime 中取节点（不绑定单一场景常量） */
export function findSceneRuntimeNode(runtime, sceneCode) {
  if (!runtime || typeof runtime !== "object" || sceneCode == null || sceneCode === "") return null;
  const code = String(sceneCode).trim();
  if (runtime[code] && typeof runtime[code] === "object") return runtime[code];
  const target = normalizeSceneKey(code);
  const hit = Object.entries(runtime).find(([key]) => normalizeSceneKey(key) === target);
  return hit && typeof hit[1] === "object" ? hit[1] : null;
}

/** 券适用场景顺序：applicableScenes 优先，否则 sceneRuntime 中的 key */
function listVoucherSceneCodes(raw) {
  const fromApplicable = parseUserCouponApplicableScenes(raw);
  if (fromApplicable.length) return fromApplicable;
  const runtime = parseJsonObject(raw?.sceneRuntime ?? raw?.scene_runtime);
  if (!runtime || typeof runtime !== "object") return [];
  return Object.keys(runtime).map((k) => String(k).trim().toUpperCase()).filter(Boolean);
}

function extractSceneCouponUrl(node) {
  if (!node || typeof node !== "object") return null;
  const couponUrl = node.couponUrl ?? node.coupon_url;
  if (!couponUrl || typeof couponUrl !== "object") return null;
  return couponUrl;
}

/** couponUrl 是否配置了任一可跳转地址 */
export function couponUrlHasJumpTarget(couponUrl) {
  if (!couponUrl || typeof couponUrl !== "object") return false;
  const keys = ["web", "WEB", "ios", "IOS", "android", "ANDROID", "h5", "H5", "common", "COMMON"];
  for (const k of keys) {
    const v = couponUrl[k];
    if (typeof v === "string" && v.trim()) return true;
    if (v && typeof v === "object" && typeof v.url === "string" && v.url.trim()) return true;
  }
  return Object.values(couponUrl).some(
    (v) =>
      (typeof v === "string" && v.trim()) ||
      (v && typeof v === "object" && typeof v.url === "string" && v.url.trim())
  );
}

/**
 * 按 applicableScenes 顺序，取首个带 couponUrl 的场景配置
 * @param {object} raw — UserCouponEntity
 * @returns {Record<string, string>|null}
 */
export function resolveVoucherSceneCouponUrl(raw) {
  if (!raw || typeof raw !== "object") return null;
  const runtime = parseJsonObject(raw.sceneRuntime ?? raw.scene_runtime);
  if (!runtime) return null;
  for (const scene of listVoucherSceneCodes(raw)) {
    const couponUrl = extractSceneCouponUrl(findSceneRuntimeNode(runtime, scene));
    if (couponUrl && couponUrlHasJumpTarget(couponUrl)) return couponUrl;
  }
  return null;
}

/** 按适用场景顺序取 sceneRuntime.remark（规则弹窗） */
export function resolveVoucherSceneRemark(raw, locale) {
  if (!raw || typeof raw !== "object") return "";
  const runtime = parseJsonObject(raw.sceneRuntime ?? raw.scene_runtime);
  if (!runtime) return "";
  for (const scene of listVoucherSceneCodes(raw)) {
    const node = findSceneRuntimeNode(runtime, scene);
    if (!node) continue;
    const fromI18n = pickCouponI18nText(node.remarkI18n ?? node.remark_i18n, locale);
    if (fromI18n) return fromI18n;
    const remark = node.remark ?? node.remark_default;
    if (remark != null && String(remark).trim()) return String(remark).trim();
  }
  return "";
}

/** @deprecated 使用 resolveVoucherSceneRemark */
export function resolveAiCopytradeSceneRemark(raw, locale) {
  return resolveVoucherSceneRemark(raw, locale);
}

/** 用户券 raw 上的规则说明：优先 sceneRuntime 场景 remark，否则 description */
export function resolveVoucherRulesContent(raw, locale) {
  if (!raw || typeof raw !== "object") return "";

  const fromScene = resolveVoucherSceneRemark(raw, locale);
  if (fromScene) return fromScene;

  const fromI18n = pickCouponI18nText(raw.descriptionI18n ?? raw.description_i18n, locale);
  if (fromI18n) return fromI18n;
  const plain =
    raw.displayDescription ??
    raw.display_description ??
    raw.description ??
    raw.templateDescription ??
    raw.template_description;
  if (plain != null && String(plain).trim()) return String(plain).trim();
  return "";
}

function normalizeRulesLineBreaks(content) {
  if (!content || typeof content !== "string") return "";
  return content.replace(/\r\n/g, "\n").replace(/\\n/g, "\n").trim();
}

function isSectionTitleLine(line) {
  return /^\d+\.\s+\S/.test(String(line || "").trim());
}

/** 按空行分段，首行为章节标题，其余为正文；兼容字面量 \\n 与「标题空行后再列要点」写法 */
export function parseVoucherRulesSections(content) {
  if (!content || typeof content !== "string") return [];
  const normalized = normalizeRulesLineBreaks(content);
  if (!normalized) return [];

  const blocks = normalized
    .split(/\n\n+/)
    .map((block) =>
      block
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
    )
    .filter((lines) => lines.length > 0);

  const merged = [];
  for (let i = 0; i < blocks.length; i += 1) {
    const lines = blocks[i];
    if (lines.length === 1 && isSectionTitleLine(lines[0]) && i + 1 < blocks.length) {
      merged.push([lines[0], ...blocks[i + 1]]);
      i += 1;
    } else {
      merged.push(lines);
    }
  }

  return merged.map((lines) => ({
    title: lines[0],
    bodyLines: lines.slice(1),
  }));
}
